const CLIENT_ERROR_MIN = 400
const CLIENT_ERROR_MAX = 499

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

// Array.isArray narrows to any[]; this keeps the element type as unknown.
const isUnknownArray = (value: unknown): value is readonly unknown[] =>
  Array.isArray(value)

// Pull an HTTP status out of whatever shape the error arrives in: an RTK Query
// FetchBaseQueryError ({ status }), a generic fetch/HTTP error ({ statusCode }),
// or a Sentry response context ({ status_code }). RTK parse failures keep the
// real code on `originalStatus`, mirroring getResponseCode() in base/baseApi.ts.
const httpStatusOf = (value: unknown): number | undefined => {
  if (!isRecord(value)) {
    return undefined
  }

  if (
    value.status === "PARSING_ERROR" &&
    typeof value.originalStatus === "number"
  ) {
    return value.originalStatus
  }

  for (const key of ["status", "statusCode", "status_code"]) {
    if (typeof value[key] === "number") {
      return value[key]
    }
  }

  return undefined
}

const hasClientErrorStatus = (value: unknown): boolean => {
  const status = httpStatusOf(value)

  return (
    status !== undefined &&
    status >= CLIENT_ERROR_MIN &&
    status <= CLIENT_ERROR_MAX
  )
}

// A 4xx is an operational client error (validation, auth, not-found) — not a bug
// worth a Sentry issue. True when any candidate carries such a status; arrays
// (e.g. captured console.error arguments) are inspected element by element.
export const isClientError = (candidates: readonly unknown[]): boolean =>
  candidates.some((candidate) =>
    isUnknownArray(candidate)
      ? isClientError(candidate)
      : hasClientErrorStatus(candidate),
  )
